import type {
  PluginListenerHandle,
  PushNotification,
  PushNotificationActionPerformed,
  PushNotificationsPlugin,
  PushNotificationToken,
} from "@capacitor/core";
import { Capacitor } from "@capacitor/core";
import gql from "graphql-tag";
import { analytics } from "lib/analytics";
import {
  extractPushNotificationData,
  PushNotificationEvent,
} from "lib/app/pushNotifications";
import { handleOpenInAppBrowser } from "lib/app/urlOpen";
import { debug } from "lib/log";
import { Sentry } from "lib/sentry";
import Router, { useRouter } from "next/router";
import React, { useCallback, useEffect, useState } from "react";
import { useMutation } from "react-apollo";
import { AppOpenEventData, schema } from "shared/analytics";
import { useApp } from "./AppContext";

type PushNotificationHandler = (notif: PushNotification) => void;

const REGISTER_DEVICE_MUTATION = gql`
  mutation registerDevice($input: RegisterDeviceInput!) {
    registerDevice(input: $input) {
      success
    }
  }
`;

const PushNotifications: PushNotificationsPlugin =
  process.browser && Capacitor.isPluginAvailable("PushNotifications")
    ? Capacitor.Plugins.PushNotifications
    : null;

const usePushNotificationsPlugin = () => {
  const [listeners, setListeners] = useState<PluginListenerHandle[]>([]);

  return {
    addListener: (
      event: PushNotificationEvent,
      handler: (...args: unknown[]) => void
    ) => {
      setListeners((listeners) => {
        return [...listeners, PushNotifications.addListener(event, handler)];
      });
    },
    removeListeners: () => {
      listeners.forEach((l: PluginListenerHandle) => l?.remove());
    },
  };
};

const useHandlePushNotifications = (handler) => {
  const { addListener, removeListeners } = usePushNotificationsPlugin();

  useEffect(() => {
    // Method called when tapping on a notification
    addListener(
      "pushNotificationActionPerformed",
      (action: PushNotificationActionPerformed) => {
        if (action.notification) {
          handler(action.notification);
        }
      }
    );

    return removeListeners;
  }, [handler]);
};

const useAppDeviceToken = (requestPermissions = false): string => {
  const { addListener, removeListeners } = usePushNotificationsPlugin();

  const [deviceToken, setDeviceToken] = useState<string>(null);

  useEffect(() => {
    if (requestPermissions && !deviceToken) {
      // The types are wrong here, `requestPermission()` is correct.
      PushNotifications.requestPermission()
        .then((result) => {
          if (result.granted) {
            debug("push notifications permissions granted");
            return PushNotifications.register();
          }
        })
        .catch((err) => {
          console.error(err);
          Sentry.captureException(err);
        });

      // On success, we should be able to receive notifications
      addListener("registration", (token: PushNotificationToken) => {
        debug("push notification token: " + token.value);
        setDeviceToken(token.value);
      });

      // Some issue with our setup and push will not work
      addListener("registrationError", (err: any) => {
        console.error(err);
        Sentry.captureException(err);
      });

      return removeListeners;
    }
  }, [requestPermissions]);

  return deviceToken;
};

interface UsePushNotificationsArgs {
  shouldRequestPermissions: boolean;
  notificationHandler: PushNotificationHandler;
}

const usePushNotifications = ({
  shouldRequestPermissions,
  notificationHandler,
}: UsePushNotificationsArgs) => {
  const { setDeviceToken } = useApp();

  const deviceToken = useAppDeviceToken(shouldRequestPermissions);

  const [registerDevice] = useMutation(REGISTER_DEVICE_MUTATION);

  useHandlePushNotifications(notificationHandler);

  useEffect(() => {
    if (deviceToken) {
      debug("got device token:", deviceToken);

      setDeviceToken(deviceToken);

      registerDevice({
        variables: {
          input: {
            platform: Capacitor.getPlatform(),
            deviceToken,
          },
        },
      }).catch((err) => {
        console.error(err);
        Sentry.captureException(err);
      });
    }
  }, [deviceToken]);
};

const PushNotificationsContext = React.createContext(null);

const Noop = ({ children }) => children;

const Provider = ({ shouldRequestPermissions, children }) => {
  const { pathname } = useRouter();
  const isBehindAuthGate =
    pathname.startsWith("/app") || pathname.startsWith("/auth");

  const notificationHandler = useCallback(
    async (notification: PushNotification) => {
      if (isBehindAuthGate) {
        debug("notification ignored due to auth gate:", notification);
        return;
      }
      debug("notification received:", notification);

      const data = extractPushNotificationData(notification);

      analytics.trackEvent<AppOpenEventData>(
        schema.APP_OPENED_BY_PUSH_NOTIFICATION,
        {
          path: data.click_action,
          appColdStart: false,
        }
      );

      if (data.open_in_browser) {
        await handleOpenInAppBrowser(data);
      } else {
        await Router.push(data.click_action);
      }
    },
    [isBehindAuthGate]
  );

  usePushNotifications({
    shouldRequestPermissions,
    notificationHandler,
  });

  return (
    <PushNotificationsContext.Provider value={null}>
      {children}
    </PushNotificationsContext.Provider>
  );
};

export default function PushNotificationsProvider({
  shouldRequestPermissions,
  children,
}) {
  const { isApp } = useApp();

  if (!process.browser) {
    return <Noop>{children}</Noop>;
  }

  if (isApp) {
    if (PushNotifications) {
      debug("push notifs enabled");
      return (
        <Provider shouldRequestPermissions={shouldRequestPermissions}>
          {children}
        </Provider>
      );
    } else {
      console.warn("no PushNotifications plugin, disabling push notifs");
    }
  } else {
    debug("push notifs disabled");
  }

  return <Noop>{children}</Noop>;
}
